import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../services/api";
import { useToast } from "../../hooks/useToast";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("14px"),
        ...FC.shorthands.padding("16px"),
    },
    row: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
    tableWrap: { overflowX: "auto" },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        "& th": {
            textAlign: "left",
            ...FC.shorthands.padding("8px"),
            borderBottom: `1px solid ${FC.tokens.colorNeutralStroke1}`,
        },
        "& td": {
            ...FC.shorthands.padding("8px"),
            borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
            verticalAlign: "top",
        },
    },
    pem: {
        maxWidth: "720px",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        fontFamily: "monospace",
        fontSize: "12px",
    },
});

const statusBadge = (status) => (
    <FC.Badge color={status === "active" ? "success" : status === "next" ? "warning" : "informative"}>
        {status}
    </FC.Badge>
);

/**
 * Global update keys are trust anchors for every tenant, so the tenant workbench links here but
 * never mutates them in tenant context. This page intentionally exposes public material only.
 */
export const UpdateSigningKeyManagement = () => {
    const styles = useStyles();
    const showToast = useToast();
    const [items, setItems] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            setItems(await apiClient.getUpdateSigningKeys() || []);
        } catch (loadError) {
            setError(loadError.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => ({
        active: items.filter((item) => item.status === "active").length,
        next: items.filter((item) => item.status === "next").length,
    }), [items]);
    const ready = counts.active === 1 && counts.next === 1;

    const generateKey = async () => {
        const kid = window.prompt("输入新的全局 Update key kid：");
        if (!kid?.trim()) return;
        const name = window.prompt("输入密钥名称：", "global update signing key");
        if (!name?.trim()) return;
        if (!window.confirm(`这是全局操作，会影响所有租户。确认生成 next Update key「${kid.trim()}」？`)) return;
        try {
            await apiClient.generateUpdateSigningKey({ kid: kid.trim(), name: name.trim() });
            showToast("全局 next Update key 已生成；私钥仅保存在服务端 secret", "success");
            await load();
        } catch (writeError) {
            showToast("生成全局 Update key 失败", "error", writeError.message);
        }
    };

    const activateKey = async (item) => {
        const reason = window.prompt(`激活 ${item.kid} 会影响所有租户并退役当前 active key。请输入原因：`);
        if (!reason?.trim()) return;
        if (!window.confirm(`确认把 ${item.kid} 激活为全局唯一 active Update key？`)) return;
        try {
            await apiClient.activateUpdateSigningKey(item.kid, {
                version: item.version,
                reason: reason.trim(),
            });
            showToast("全局 Update key 已激活", "success");
            await load();
        } catch (writeError) {
            showToast("激活全局 Update key 失败", "error", writeError.message);
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.row}>
                <FC.Title2>全局 Update signing keys</FC.Title2>
                <FC.Button appearance="primary" icon={<IC.KeyRegular />} onClick={generateKey}>生成 next key</FC.Button>
                <FC.Button icon={<IC.ArrowSyncRegular />} onClick={load}>刷新</FC.Button>
            </div>
            <FC.MessageBar intent={ready ? "success" : "warning"}>
                <FC.MessageBarBody>
                    当前 active {counts.active} / next {counts.next}；设备包要求全局恰有一把 active 和一把 next key。
                    此页只展示全局公钥状态，不显示任何私钥或 secret locator。
                </FC.MessageBarBody>
            </FC.MessageBar>
            {error && <FC.MessageBar intent="error"><FC.MessageBarBody>{error}</FC.MessageBarBody></FC.MessageBar>}
            {loading ? <FC.Spinner label="加载全局密钥..." /> : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead><tr><th>kid</th><th>名称</th><th>状态</th><th>版本</th><th>SPKI SHA-256</th><th>公钥</th><th>更新时间</th><th>操作</th></tr></thead>
                        <tbody>{items.map((item) => (
                            <tr key={item.id}>
                                <td>{item.kid}</td>
                                <td>{item.name}</td>
                                <td>{statusBadge(item.status)}</td>
                                <td>{item.version}</td>
                                <td>{item.publicKeyFingerprint}</td>
                                <td>
                                    <details>
                                        <summary>查看公钥 PEM</summary>
                                        <pre className={styles.pem}>{item.publicKeyPem}</pre>
                                    </details>
                                </td>
                                <td>{item.updatedAtUtc ? new Date(item.updatedAtUtc).toLocaleString() : "-"}</td>
                                <td><FC.Button disabled={item.status !== "next"} onClick={() => activateKey(item)}>激活</FC.Button></td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
